/**
 * メール生成システム専用データモデル型定義。
 * 仕様書: docs/01_spec/spec_mail_system.md B-2
 */

import type { IsoOrTimestamp } from './common';
import type {
  AnalysisAxisId,
  FooterSeparator,
  MailLang,
  MailSectionType,
  MailStatus,
} from '../constants';

// ─────────────────────────────────────────────
// テンプレート / プレセット → 統一プロンプト管理（shared/src/types/prompt.ts）に移行済み
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// mails
// ─────────────────────────────────────────────
export interface MailSection {
  id: string;
  type: MailSectionType;
  label: string;
  content: string;
  cta_url_placeholder?: string; // CTA セクションのみ
  cta_button_label?: string; // CTA セクションのみ。HTML メールのボタンに表示するラベル
  cta_position_note?: string; // CTA セクションのみ
}

export interface Mail {
  subject: string;
  sections: MailSection[];
  overview: string;
  field_comments: Record<string, string>;
  templateId: string;
  formData: Record<string, string>;
  lang: MailLang;
  modelUsed: string;
  sectionComments: Record<string, string>;
  note: string;
  status: MailStatus;
  projectIds: string[];
  sentAt: IsoOrTimestamp | null;
  sentTo: string[] | null;
  _deleted: boolean;
  _deletedAt: IsoOrTimestamp | null;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  createdBy: string;
}

export type ReviewRequestStatus = 'pending' | 'approved' | 'rejected';

export interface ReviewRequest {
  toStaff: { name: string; email: string };
  comment: string;
  sentAt: IsoOrTimestamp;
  status: ReviewRequestStatus;
  reviewedAt: IsoOrTimestamp | null;
  reviewerComment: string | null;
}

// ─────────────────────────────────────────────
// projects / analyses
// ─────────────────────────────────────────────
export interface MailProject {
  name: string;
  color: string; // hex
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  createdBy: string;
}

export interface AnalysisResultItem {
  title: string;
  summary: string;
  details: string;
  recommendations: string[];
  score: number; // 0-100
}

export interface MailAnalysis {
  mailId: string;
  axes: AnalysisAxisId[];
  results: Record<string, AnalysisResultItem>;
  modelUsed: string;
  createdAt: IsoOrTimestamp;
  createdBy: string;
}

// ─────────────────────────────────────────────
// settings（Mail 側は mail_settings/{docId} の単一ドキュメント群）
// ─────────────────────────────────────────────
export interface MailCompanySettings {
  name: string;
  representative: string;
  postalCode: string;
  address: string;
  phone: string;
  email: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export interface MailStaffMember {
  name: string;
  email: string;
}

export interface MailStaffSettings {
  list: MailStaffMember[];
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export interface MailFooterSettings {
  separator: FooterSeparator;
  logoUrl: string;
  webUrl: string;
  snsLinks: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

export interface MailPreferencesSettings {
  defaultModel: string;
  defaultLang: MailLang;
  softDeleteRetentionDays: number;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

// ─────────────────────────────────────────────
// Gmail OAuth（KMS 暗号化必須、docs/01_spec/spec_mail_system.md B-2.4）
// 旧設計: mail_gmail_tokens/{uid}（1 ユーザー = 1 アドレス）
// 新設計: mail_gmail_tokens/{accountId}（複数アドレス対応 / Phase 26）
// ─────────────────────────────────────────────
export interface MailGmailToken {
  encryptedAccessToken: string; // Cloud KMS 暗号化済み Base64
  encryptedRefreshToken: string;
  kmsKeyVersion: string;
  scope: string;
  authorizedEmail: string;
  expiryDate: IsoOrTimestamp;
  authorizedAt: IsoOrTimestamp;
  revokedAt: IsoOrTimestamp | null;
}

// ─────────────────────────────────────────────
// 送信元メールアカウント（Phase 26：複数アドレス + SMTP 対応）
// ─────────────────────────────────────────────
export type MailAccountProvider = 'gmail_oauth' | 'smtp';
export type MailAccountStatus =
  | 'active'         // 利用可能
  | 'pending_oauth'  // Gmail 認証フロー中
  | 'unauthorized'   // 認証失敗 / SMTP 連続失敗
  | 'revoked';       // ユーザーが解除済み

export interface MailAccount {
  /** 表示名（UI で識別しやすい呼称） */
  displayName: string;
  /** 送信元メールアドレス（From ヘッダ） */
  email: string;
  provider: MailAccountProvider;
  status: MailAccountStatus;
  /** 配信予約フォームでプリセレクトされる既定アカウント（コレクション内 1 件のみ true） */
  isDefault: boolean;
  /** 直近 N 回の連続送信失敗。SMTP の自動 unauthorized 切替に使用 */
  consecutiveFailures?: number;
  /** 直近のエラー文（短縮済） */
  lastError?: string | null;
  /**
   * 1 予約あたりの宛先数上限の個別上書き（任意）。
   * 未設定なら provider 別デフォルト（MAIL_RECIPIENT_CAP_DEFAULTS）を使う。
   * SMTP リレー（Amazon SES 等）で送信枠が大きい場合に引き上げる用途。
   */
  dailySendLimit?: number;
  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/**
 * 送信元アカウント種別ごとの「1 予約あたり宛先数」上限デフォルト。
 * - gmail_oauth: Google の 1 日送信上限（Workspace 約 2000 通/日）がハード制限のため 2000。
 * - smtp:        Amazon SES 等のリレーは日量が大きいので高めに許可（個別 dailySendLimit で調整）。
 */
export const MAIL_RECIPIENT_CAP_DEFAULTS: Record<MailAccountProvider, number> = {
  gmail_oauth: 2000,
  smtp: 10000,
};

/**
 * 送信元アカウントの実効上限（1 予約あたり宛先数）を返す。
 * dailySendLimit があればそれを優先、無ければ provider 別デフォルト。
 */
export function mailRecipientCap(account: {
  provider: MailAccountProvider;
  dailySendLimit?: number | null;
}): number {
  if (typeof account.dailySendLimit === 'number' && account.dailySendLimit > 0) {
    return account.dailySendLimit;
  }
  return MAIL_RECIPIENT_CAP_DEFAULTS[account.provider];
}

/**
 * SMTP 認証情報（mail_smtp_credentials/{accountId}）。
 * password は KMS 暗号化済みで保存。Cloud Function のみ read/write 可。
 */
export interface MailSmtpCredential {
  host: string;
  port: number;
  /** true: SMTPS (port 465 等) / false: STARTTLS (port 587 等) */
  secure: boolean;
  username: string;
  encryptedPassword: string; // KMS 暗号化済み Base64
  kmsKeyVersion: string;
  /** From ヘッダの表示名（display name） */
  fromName?: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// 送信ログ
// ─────────────────────────────────────────────
export type MailSendStatus = 'success' | 'failed';
export type MailSendType = 'production' | 'test' | 'review';

export interface MailSendLog {
  mailId: string;
  type: MailSendType;
  to: string[];
  cc: string[];
  bcc: string[];
  fromEmail: string;
  sentBy: string;
  gmailMessageId: string | null;
  status: MailSendStatus;
  errorMessage: string | null;
  timestamp: IsoOrTimestamp;
  /**
   * 紐付くキャンペーン ID。予約送信 (sendIndividualTask) 経由で
   * mail_schedules.campaignId をコピーする。即時送信時は省略。
   */
  campaignId?: string | null;
}

// ─────────────────────────────────────────────
// 配信先アドレス管理（Phase 18 / spec_mail_system.md B-10）
// ─────────────────────────────────────────────
export type MailContactSource = 'csv' | 'manual' | 'form';

/**
 * 営業ツール (Phase 2+) の論理的分離フィールド用 type。
 * 物理コレクションは mail_contacts のまま、promotionLevel で role を分離する。
 *
 * 自動遷移ルール:
 *   - lead → customer: 個別説明申込 / 見積依頼 form 送信時
 *   - customer → inactive: 失注 + 1年 activity なし、または手動
 *   - inactive → customer: 再フォーム送信で自動復帰
 *   - customer → lead: 自動降格なし、人間判断のみ
 */
export type ContactPromotionLevel = 'lead' | 'customer' | 'inactive';

/** 顧客タイプ。BtoB は法人、BtoC は個人、unknown は判定保留。 */
export type ContactKind = 'btob' | 'btoc' | 'unknown';

/**
 * メール添付ファイル（v3.x / 2026-07。送金完了メールの証憑画像等）。
 * クライアント → Callable → 送信層へ base64 で渡す。
 */
export interface MailAttachment {
  filename: string;
  /** base64（改行なし）。 */
  contentBase64: string;
  /** 例: "image/png", "image/jpeg", "application/pdf"。 */
  contentType: string;
}

export interface MailContactEmail {
  /** 小文字化・trim 済みのメールアドレス */
  address: string;
  /** この address が追加 or 再検出された時刻（最も新しいものが defaultEmail 候補） */
  addedAt: IsoOrTimestamp;
}

/**
 * 配信停止（オプトアウト）を表す予約タグ。
 * 配信停止フォーム送信時に mail_contacts.tags へ付与され、一括/予約配信（resolveRecipients）の
 * tag / group 解決時に、このタグを持つ contact は自動的に宛先から除外される。
 * 別のマーケティングフォームを送信すると（再関心の表れとみなして）自動的に除去される。
 */
export const UNSUBSCRIBE_TAG = '配信停止';

export interface MailContact {
  /** 表示名。同名・別メアドの統合キーに使う */
  name: string;
  /** 1 contact に複数メアドを許可。最も addedAt が新しいものが既定配信先 */
  emails: MailContactEmail[];
  /** emails の中で最も新しい address。CSV 取り込み毎に自動更新 */
  defaultEmail: string;
  /** CSV 取り込み時の任意列を保持（同キー再取り込み時は新しい値で上書き） */
  customFields: Record<string, string>;
  /** importTitle 由来 + 手動付与のタグ。フォーム送信時の自動付与は廃止（Phase 24e） */
  tags: string[];
  /**
   * 所属グループ ID 配列。
   * mail_groups.memberContactIds と双方向同期する（書込時バッチ + 削除時トリガー）。
   * 仕様書: docs/01_spec/spec_mail_system.md B-11（Phase 19 ハイブリッド方式）
   */
  groupIds?: string[];
  /**
   * 回答済フォーム ID の集合（Phase 24e）。
   * onFormSubmissionCreated で arrayUnion される。
   * 「このフォームに答えた人」の検索インデックスとして利用。
   */
  formIds?: string[];
  /**
   * 関連キャンペーン ID の集合（Phase 24e）。
   * フォーム送信時に form.campaignIds[] と submission.campaignId の和集合を arrayUnion。
   * 「このキャンペーンに関わった人」の検索インデックスとして利用。
   */
  campaignIds?: string[];
  /** LINE 公式アカウント連携時の userId（Phase 24f 以降で使用予定） */
  lineUid?: string;
  source: MailContactSource;

  // ─── 営業ツール (Phase 2+) ───
  /**
   * 顧客タイプ。Phase 2 では未設定 (undefined) を 'unknown' 扱い。
   * 個別説明申込・見積依頼の送信時に自動推定 or 手動設定。
   */
  kind?: ContactKind;
  /**
   * マーケ/営業の論理分離。
   * 未設定 (undefined) は 'lead' 扱い (後方互換)。
   * resolveDealForSubmission が個別説明・見積送信時に 'customer' に昇格。
   */
  promotionLevel?: ContactPromotionLevel;
  /** customer 昇格日時 (初回のみ設定)。 */
  promotedToCustomerAt?: IsoOrTimestamp | null;
  /** 初契約日 (初の Deal が won になった時に設定、再契約では更新しない)。 */
  customerSince?: IsoOrTimestamp | null;
  /** 休眠化日時 (markInactiveContacts または手動)。 */
  inactivatedAt?: IsoOrTimestamp | null;
  /**
   * 動作確認用のテスト顧客フラグ (2026-08-27+)。
   * 未設定 (undefined) / false は通常の顧客 (後方互換)。判定は必ず `=== true` で行う。
   *
   * true のとき resolveDealForSubmission は既存 Deal の候補スコアリングを丸ごとスキップし、
   * 「候補0件」経路に落とす。結果として
   *   - high 自動マージ (mergeIntoExistingDeal) が起きない
   *   - deal_merge_candidates (保留トレイ) に積まれない → 担当者への通知も飛ばない
   *   - フォーム送信のたびに必ず新規 Deal が作られる
   * ので、同じ顧客で個別説明・見積の申込を何度でも通し確認できる。
   *
   * 本番の顧客には付けないこと (その顧客の重複検知が無警告で無効化される)。
   * 保留トレイ確定 (resolveMergeCandidate) は staff の明示操作なのでこのフラグを見ない。
   */
  isTestContact?: boolean;
  /**
   * 営業向け背景記録 (Phase A / P-C 追加)。
   * 顧客詳細「基本情報」タブで編集可能。商談横断でこの顧客固有のコンテキストを残す自由記述欄。
   * 例: 「決裁者は社長、技術担当は田中さん。導入は会計年度末を希望」
   */
  salesMemo?: string | null;
  /**
   * この顧客を担当する SA (外部営業) uid。v3.2.23+。
   * admins/{uid} (dept='sales' && level='external') を参照。
   * SA リファラル経由フォーム送信 or SA 手動追加 (createContactBySa) でセット。
   * `/sa/contacts` 画面で `where('ownerSaUid', '==', uid)` フィルタの索引キー。
   * null / undefined は「社内が管理する顧客」(SA 担当なし) を意味する。
   *
   * 付与ポリシーは「先勝ち」。既に別 SA が担当の顧客は上書きしない
   * (訂正は顧客詳細＞基本情報＞担当SA から owner が手動で行う)。
   */
  ownerSaUid?: string | null;
  /**
   * ownerSaUid の表示名スナップショット (2026-07-28+)。
   * admins は owner のみ read 可のため、editor でも顧客詳細で担当SA名を出せるよう非正規化する。
   * 正は admins/{ownerSaUid}。SA 側で改名された場合、owner 画面は実データ優先で表示する。
   */
  ownerSaName?: string | null;
  /** ownerSaUid を付与した経路。'form' = フォーム自動 / 'manual' = SA 追加・管理者訂正。 */
  ownerSaAssignedVia?: 'form' | 'manual' | null;
  /** ownerSaUid を付与した日時。 */
  ownerSaAssignedAt?: IsoOrTimestamp | null;

  // ─── SA 顧客保有期間 (2026-07-28) ───
  /**
   * SA 保有期限。この日時を過ぎると saRetentionSweep が ownerSaUid を外す。
   * null / undefined = 無期限 (settings/sales.saCustomerRetentionMonths = 0 の場合など)。
   * 起算は「SA に紐付いた時点」と「その SA の商談で入金があった時点」。
   */
  saRetentionUntil?: IsoOrTimestamp | null;
  /** saRetentionUntil を算出した際の保有期間 (ヶ月)。設定変更の履歴追跡用。 */
  saRetentionMonths?: number | null;
  /** 保有期限の起算日時 (最後にリセットされた時点)。 */
  saRetentionStartedAt?: IsoOrTimestamp | null;
  /** 保有期限を最後に設定/リセットした理由。 */
  saRetentionReason?: 'assigned' | 'payment' | 'manual_extend' | null;
  /**
   * 期限切れ間近リマインダーを送信済みの期限日時 (ISO)。
   * saRetentionUntil と一致する間は再送しない。リセットで期限が動けば再び送られる。
   */
  saRetentionReminderSentFor?: string | null;

  /** 期限切れで SA から解除された日時 (直近)。 */
  saReleasedAt?: IsoOrTimestamp | null;
  /** 解除前の担当SA uid (履歴)。 */
  saReleasedFromSaUid?: string | null;
  /** 解除前の担当SA 表示名 (履歴)。 */
  saReleasedFromSaName?: string | null;

  // BtoB 専用
  companyName?: string | null;
  /** 商号正規化済み (株式会社 → (株) 等)。重複判定用 (Phase 4)。 */
  companyNameNormalized?: string | null;
  department?: string | null;
  jobTitle?: string | null;

  // 共通住所（後方互換用 / v3.2.0 以降は billingAddresses が真のソース）
  /** @deprecated v3.2.0 以降は billingAddresses[isDefault].postalCode を使用。読み取り互換用に残置。 */
  postalCode?: string | null;
  /** @deprecated v3.2.0 以降は billingAddresses[isDefault].prefecture を使用。 */
  prefecture?: string | null;
  /** @deprecated v3.2.0 以降は billingAddresses[isDefault].address を使用。 */
  address?: string | null;
  /** @deprecated v3.2.0 以降は billingAddresses[isDefault].buildingName を使用。 */
  buildingName?: string | null;

  /**
   * 請求先住所 (複数登録可、v3.2.0 追加)。
   * 請求書・見積書の宛先住所として使用。
   * `isDefault: true` のエントリは「既定の請求先」として優先利用される (1件のみ)。
   * 未設定の旧データは postalCode/prefecture/address/buildingName から読み取り互換シムで補完される (getBillingAddresses)。
   */
  billingAddresses?: BillingAddress[];

  /**
   * 納品先住所 (複数登録可、v3.1.6 追加)。
   * 商品の発送・納品で使用。請求住所とは別に、複数の現場/倉庫/営業所への納品を想定している。
   * `isDefault: true` のエントリは「既定の納品先」として優先利用される (1件のみ)。
   */
  deliveryAddresses?: DeliveryAddress[];

  // BtoC 補助
  birthdate?: string | null; // YYYY-MM-DD

  // ─── 個人プロファイル拡張 (v3.3.x / CSV インポートのデフォルト構造化フィールド) ───
  /** 姓 (Last Name)。空なら name (氏名) を分割して取得。 */
  firstName?: string | null;
  lastName?: string | null;
  /** 姓 (フリガナ・カナ)。 */
  firstNameKana?: string | null;
  lastNameKana?: string | null;
  /** 連絡先電話番号 (本人。billingAddress.phone とは別軸)。 */
  phone?: string | null;
  /** 職業 (例: 会社員, 自営業, NES プラクティショナー)。 */
  occupation?: string | null;
  /** 性別 (例: 男性, 女性, その他)。 */
  gender?: string | null;
  /**
   * 個人住所 (BtoC ライト用)。BtoB 用の billingAddresses とは別軸で、
   * CSV インポートの「郵便番号 / 都道府県 / 市区町村 / 町名・番地」列をここに格納。
   */
  personalAddress?: {
    postalCode?: string | null;
    prefecture?: string | null;
    city?: string | null;
    line?: string | null;
  } | null;

  // ─── 英表記登録情報 (v3.x / 2026-07。海外メーカーへの送金完了報告メール等で使う) ───
  /** 英語表記の氏名 (例: "Yuki Teraoka")。送金完了メールの顧客名に使用。 */
  nameEn?: string | null;
  /** 国番号付き電話番号 (例: "+81 90-1234-5678")。国内 phone とは別軸。 */
  phoneIntl?: string | null;
  /** 英語表記の住所 (国別フィールド)。 */
  englishAddress?: EnglishAddress | null;

  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/**
 * 英語表記の住所 (v3.x / 2026-07)。
 *
 * 国 (country) を軸に、主要国ごとの入力フィールドを出し分けるためのスーパーセット。
 * 実際にどのフィールドを表示・どのラベルにするかは
 * `COUNTRY_ADDRESS_CONFIGS` (utils/english-address.ts) が国別に定義する。
 * データは常にこの共通スキーマで保存し、UI 側で国別に見せ方を変える。
 */
export interface EnglishAddress {
  /** 国コード (例: "JP", "US", "DE") または国名。 */
  country?: string | null;
  postalCode?: string | null;
  /** 都道府県 / State / Province / Region。 */
  stateRegion?: string | null;
  /** 市区町村 / City。 */
  city?: string | null;
  /** 番地・町名 / Street address。 */
  line1?: string | null;
  /** 建物名・部屋番号 / Apt, Suite, Building。 */
  line2?: string | null;
}

/**
 * 納品先住所 (MailContact.deliveryAddresses の要素)。
 *
 * - id はクライアント側で UUID を生成して付与する (Firestore は配列要素にネストしたドキュメント ID を持たないため)
 * - label は表示用の任意ラベル (例: "本社", "倉庫A", "東京営業所")
 * - 各フィールドは MailContact 直下の住所と同じ意味
 */
export interface DeliveryAddress {
  id: string;
  label?: string | null;
  recipientName?: string | null;
  postalCode?: string | null;
  prefecture?: string | null;
  address?: string | null;
  buildingName?: string | null;
  phone?: string | null;
  /** 既定の納品先フラグ。true は配列内で最大 1 件まで。 */
  isDefault?: boolean;
}

// ─────────────────────────────────────────────
// 顧客フィールド変更履歴 (2026-07-28)
// ─────────────────────────────────────────────

/** 変更を発生させた経路。現状はフォーム送信のみが自動書込を行う。 */
export type ContactFieldChangeVia = 'form';

/**
 * mail_contacts/{contactId}/field_changes/{autoId}。
 *
 * フォーム送信による顧客情報の自動更新は破壊的になりうるため、
 * 上書き・追加が起きた枠だけを 1 ドキュメント 1 フィールドで記録する。
 * 誤った上書きを人が確認・復元するための監査ログ (自動ロールバックはしない)。
 *
 * 値は表示用に文字列化して保存する (住所・英語住所は 1 行に整形)。
 */
export interface ContactFieldChange {
  /** MailContact 上のフィールド名 (例: 'phone', 'billingAddresses')。 */
  field: string;
  /** 画面表示用のラベル (例: '電話番号', '請求先住所')。 */
  fieldLabel: string;
  /** 変更前の値 (文字列化)。新規追加なら null。 */
  before: string | null;
  /** 変更後の値 (文字列化)。 */
  after: string;
  /** 'overwrite' = 既存値を置換 / 'fill' = 空欄を補完 / 'append' = 配列に追加して既定化。 */
  mode: 'overwrite' | 'fill' | 'append';
  via: ContactFieldChangeVia;
  formId?: string | null;
  formName?: string | null;
  submissionId?: string | null;
  changedAt: IsoOrTimestamp;
}

// ─────────────────────────────────────────────
// ドイツ (メーカー) 報告記録 (2026-08-24)
// ─────────────────────────────────────────────

/**
 * メーカー (ドイツ) へ報告した内容のスナップショット。
 * 報告対象は 電話番号 / メールアドレス / 氏名(英語表記) / 住所(英語表記) の 4 項目。
 * 報告時点の値をそのまま凍結保存する (顧客データが後から変わっても記録は不変)。
 */
export interface GermanyReportSnapshot {
  /** 電話番号 (MailContact.phone)。 */
  phone: string | null;
  /** メールアドレス (MailContact.defaultEmail)。 */
  email: string | null;
  /** 氏名・英語表記 (MailContact.nameEn)。 */
  nameEn: string | null;
  /** 住所・英語表記 (MailContact.englishAddress)。 */
  englishAddress: EnglishAddress | null;
}

/**
 * mail_contacts/{contactId}/germany_reports/{autoId} — ドイツ報告記録 (追記専用台帳)。
 *
 * 「いつ・誰が・どの内容でメーカー (ドイツ) へ報告したか」を 1 報告 = 1 ドキュメントで記録する。
 *   - 書込は担当者の明示操作 (顧客詳細 > ドイツ報告 タブの「報告済みとして記録」) のみ。
 *     フォーム送信等の自動反映では記録されない。
 *   - firestore.rules で update / delete 禁止 (create のみ)。古い記録は上書きせず履歴として残る。
 *   - 現在の顧客データと直近記録の差異判定は shared/utils/germany-report.ts の
 *     diffGermanyReport() で行う (画面表示用)。
 */
export interface GermanyReport {
  /** 報告した内容 (報告時点の顧客データの凍結コピー)。 */
  snapshot: GermanyReportSnapshot;
  /** 記録操作を行った管理者 uid。rules で request.auth.uid と一致必須。 */
  reportedByUid: string;
  /** 記録操作を行った管理者の表示名スナップショット (admins は owner 以外 read 不可のため)。 */
  reportedByName: string | null;
  /** 任意メモ (例: 「お客様依頼で住所変更後に再報告」)。 */
  note?: string | null;
  /** 報告日時 (記録時に serverTimestamp)。 */
  reportedAt: IsoOrTimestamp;
}

/**
 * 請求先住所 (MailContact.billingAddresses の要素、v3.2.0 追加)。
 *
 * DeliveryAddress と同形だが、請求書・見積書の宛先として使用される。
 * 旧データ (postalCode/prefecture/address/buildingName 直下) からの読み取りは getBillingAddresses ヘルパーで互換補完される。
 */
export interface BillingAddress {
  id: string;
  label?: string | null;
  recipientName?: string | null;
  postalCode?: string | null;
  prefecture?: string | null;
  address?: string | null;
  buildingName?: string | null;
  phone?: string | null;
  /** 既定の請求先フラグ。true は配列内で最大 1 件まで。 */
  isDefault?: boolean;
}

// ─────────────────────────────────────────────
// 配信先グループ管理（Phase 19 / spec_mail_system.md B-11）
// ─────────────────────────────────────────────
export type MailGroupType = 'manual' | 'ai_keyword';

export interface MailGroup {
  name: string;
  description?: string;
  type: MailGroupType;
  /** AI モード（type='ai_keyword'）時に使用したキーワード */
  keywords?: string[];
  /** 確定メンバーの mail_contacts ドキュメント ID 配列 */
  memberContactIds: string[];
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  createdBy: string;
}

// ─────────────────────────────────────────────
// 送信予約（Phase 20 / spec_mail_system.md B-12）
// ─────────────────────────────────────────────
export type MailScheduleTargetType = 'tag' | 'group' | 'addresses';
export type MailScheduleStatus =
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'failed'
  | 'canceled';

export interface MailSchedule {
  /** 送信対象のメール（mails/{id}） */
  mailId: string;
  /** 一覧表示用 denormalize（mail.subject のスナップショット） */
  subject: string;
  targetType: MailScheduleTargetType;
  /** targetType に応じて: tag 名 配列 / [groupId] / メアド配列 */
  targetIds: string[];
  /** 一覧表示用 denormalize */
  recipientCount: number;
  /**
   * 送信元アカウント ID (mail_accounts/{id})。
   * Phase 26 で fromAddress（文字列）から ID 参照へ移行。
   */
  fromAccountId: string;
  /**
   * 送信元メアド（display / 監査用にスナップショット）。
   * 旧スキーマ互換。実際の送信は fromAccountId からプロバイダ情報を解決して行う。
   */
  fromAddress: string;
  /** {{CTA_URL}} 差し替え用（任意） */
  ctaUrl?: string;
  /**
   * 送信控え宛先メアド（任意）。
   * 配信実行時に、配信内容と同一の控えメールがこの宛先へ 1 通だけ届く
   * （件名に【送信控え】が付く。宛先ごとの CC 付与は行わない）。
   */
  cc?: string[];
  sendAt: IsoOrTimestamp;
  status: MailScheduleStatus;
  /** 送信試行回数（最大 3） */
  attempts: number;
  lastError?: string;
  /** 成功送信数（実行後に書き戻し） */
  sentRecipientCount?: number;
  /** 失敗送信数（無効アドレス 501 等・最終試行失敗の合計）。完了判定 sent+failed>=recipientCount に使う。 */
  failedRecipientCount?: number;
  /** 送信完了時刻（status='sent' へ遷移した時に記録） */
  completedAt?: IsoOrTimestamp;
  /**
   * 紐付くキャンペーン ID（plan adaptive-crown §機能 3）。
   * dispatcher が mail_send カードから schedule を作成する際にコピー、
   * sendIndividualMailTask 経由で audience_events.campaignId に伝搬される。
   */
  campaignId?: string | null;
  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}
